import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FOOD_TYPES = ["restaurant", "cafe", "bakery", "street_food", "market", "food_hall", "food_tour", "cooking_class", "grocery", "farmers_market"];
const STAY_TYPES = ["hotel", "boutique_hotel", "resort", "hostel", "homestay", "guesthouse", "bnb", "apartment", "villa", "farm_stay", "heritage", "houseboat", "campground", "glamping", "eco_lodge"];
const EMPTY = { kind: "food", name: "", business_type: "", destination: "", area: "", address: "",
  website: "", phone: "", contact_email: "", description: "", price_level: "", submitter_role: "owner" };

/* Businesses (or travelers with a find) submit a listing for admin review. */
export default function BusinessSubmit() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [f, setF] = useState({ ...EMPTY, kind: params.get("kind") === "stay" ? "stay" : "food" });
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name.trim() || !f.destination.trim()) { toast.error("Business name and destination are required"); return; }
    setSaving(true);
    try { await api.post("/business/submissions", f); setSent(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setSaving(false); }
  };

  const inp = "border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary w-full";
  const types = f.kind === "food" ? FOOD_TYPES : STAY_TYPES;

  if (!user) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="submit-business-signin">
      <h1 className="font-heading font-900 text-2xl">Sign in to submit a business</h1>
      <p className="text-sm text-muted-foreground mt-2">You'll be notified when your listing is reviewed.</p>
    </div>
  );
  if (sent) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="submit-business-done">
      <h1 className="font-heading font-900 text-2xl">Thank you — submission received</h1>
      <p className="text-sm text-muted-foreground mt-2">Our team will inspect the details. You'll get a notification (and an email if provided) when it's published or if we need changes.</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-5 lg:px-8 py-10" data-testid="submit-business-page">
      <h1 className="font-heading font-900 text-3xl flex items-center gap-2 mb-1"><Building2 className="w-8 h-8 text-teal-700" /> Submit a Business</h1>
      <p className="text-sm text-muted-foreground mb-6">Restaurants, cafés, markets, hotels, homestays, guesthouses — submit complete information for review. Approved listings appear in our destination guides and you'll be notified.</p>

      <div className="space-y-3">
        <div className="flex gap-2" data-testid="submit-kind">
          {[["food", "Food & Dining"], ["stay", "Hotels & Accommodation"]].map(([k, l]) => (
            <button key={k} onClick={() => setF({ ...f, kind: k, business_type: "" })} data-testid={`submit-kind-${k}`}
              className={`px-4 py-2 text-sm font-700 border ${f.kind === k ? "bg-teal-700 text-white border-teal-700" : "border-zinc-300 hover:border-teal-500"}`}>{l}</button>
          ))}
        </div>
        <input value={f.name} onChange={set("name")} data-testid="submit-name" placeholder="Business name *" className={inp} />
        <div className="grid sm:grid-cols-2 gap-2">
          <select value={f.business_type} onChange={set("business_type")} data-testid="submit-type" className={`${inp} bg-white`}>
            <option value="">Business type…</option>
            {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <input value={f.destination} onChange={set("destination")} data-testid="submit-destination" placeholder="Destination (e.g. Kochi) *" className={inp} />
          <input value={f.area} onChange={set("area")} placeholder="Area / neighborhood" className={inp} />
          <input value={f.address} onChange={set("address")} placeholder="Address" className={inp} />
          <input value={f.website} onChange={set("website")} data-testid="submit-website" placeholder="Website / booking link" className={inp} />
          <input value={f.phone} onChange={set("phone")} placeholder="Phone" className={inp} />
          <input value={f.contact_email} onChange={set("contact_email")} data-testid="submit-email" placeholder="Contact email (for review updates)" className={inp} />
          <select value={f.price_level} onChange={set("price_level")} className={`${inp} bg-white`}>
            <option value="">Price level…</option>
            {(f.kind === "food" ? ["budget", "moderate", "upscale"] : ["budget", "moderate", "upper_mid", "upscale", "luxury"]).map((p) => <option key={p} value={p}>{p.replace("_", " ")}</option>)}
          </select>
        </div>
        <textarea value={f.description} onChange={set("description")} data-testid="submit-description" rows={4}
          placeholder="Describe the business — what makes it special, signature dishes / room types, opening details…" className={`${inp} resize-y`} />
        <div className="flex items-center gap-3 text-sm" data-testid="submit-role">
          <span className="font-700">I am:</span>
          {[["owner", "The owner"], ["manager", "A manager"], ["traveler", "A traveler recommending it"]].map(([r, l]) => (
            <label key={r} className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="radio" value={r} data-testid={`submit-role-${r}`} checked={f.submitter_role === r} onChange={() => setF({ ...f, submitter_role: r })} /> {l}
            </label>
          ))}
        </div>
        <button onClick={submit} disabled={saving} data-testid="submit-send"
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-700 text-white bg-teal-700 hover:bg-teal-800 disabled:opacity-50">
          <Send className="w-4 h-4" /> {saving ? "Submitting…" : "Submit for review"}
        </button>
      </div>
    </div>
  );
}
